<template>
  <v-row
    no-gutters
    class="percent-change"
    :class="{ hidden: percentChange === null }"
    data-cy="percentChange"
  >
    <v-icon v-if="percentChange > 0" class="arrow-icon text-primary" :icon="'mdi-arrow-up-bold'" />
    <v-icon
      v-if="percentChange < 0"
      class="arrow-icon text-primary"
      :icon="'mdi-arrow-down-bold'"
    />
    <span v-if="percentChange !== 0" class="change"> {{ Math.abs(percentChange) }}% </span>
    <span v-if="percentChange === 0">
      {{ $t('map.popover.percentChange.noChange') }}
    </span>
    <span class="message">
      {{ $t('map.popover.percentChange.last24Hours') }}
    </span>
  </v-row>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { measurementTypes, aqiTypes } from '@/store/map/utils'
import { NAMESPACES } from '@/store/types'
import { MAP_GETTERS } from '@/store/map'
import { DEVICE_ACTIONS, DEVICE_GETTERS } from '@/store/devices'
import type { ChangeOverTime } from '@/types/devices'
import type { GetterByDeviceId } from '@/components/devicePopover/types'

export default defineComponent({
  name: 'aqiPercentChanged',
  props: {
    deviceId: { type: String, default: '' },
    aqiType: { type: Number, default: 0 }
  },
  computed: {
    id(): string {
      return this.deviceId || ''
    },
    percentChange(): number | null {
      const measType = this.$store.getters[
        `${NAMESPACES.MAP}/${MAP_GETTERS.GET_MEASUREMENT_TYPE}`
      ] as number
      const isIndia = this.aqiType === aqiTypes.in

      const getter = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_PERCENT_CHANGED_BY_DEVICE_ID}`
      ] as GetterByDeviceId<ChangeOverTime>
      const perBlob = getter(this.id)
      if (!perBlob) return null
      if (measType === measurementTypes.pm25) {
        const key = isIndia ? 'pm2x5_india_aqi_percent_change' : 'pm2x5_aqi_percent_change'
        if (perBlob[key] == 0) return 0
        if (perBlob[key] != null) {
          return Math.round(perBlob[key] * 100)
        }
      }

      if (measType === measurementTypes.pm10) {
        const key = isIndia ? 'pm10_india_aqi_percent_change' : 'pm10_aqi_percent_change'
        if (perBlob[key] == 0) return 0
        if (perBlob[key] != null) {
          return Math.round(perBlob[key] * 100)
        }
      }

      return null
    }
  },
  methods: {
    loadData(): void {
      if (this.deviceId) {
        this.$store.dispatch(
          `${NAMESPACES.DEVICES}/${DEVICE_ACTIONS.GET_PERCENT_CHANGED_AND_UPDATE}`,
          this.deviceId
        )
      }
    }
  }
})
</script>

<style lang="scss" scoped>
.percent-change {
  opacity: 1;
  transition: opacity 500ms;
  height: 18px;
  margin-top: 12px;
  margin-left: 5px;
  font-size: 13px;
  font-weight: 300;
  .arrow-icon {
    font-size: 16px;
  }
  span {
    margin-left: 4px;
    &:first-child {
      margin-left: 3px;
    }
  }
  .change {
    font-weight: 600;
  }
  @media (max-width: 460px) {
    margin-top: 8px;
  }
}

.hidden {
  opacity: 0;
  transition: opacity 0ms;
}
</style>
